// Schema-driven lead form configuration. One entry per lead_type. Each entry
// describes the modal copy and the EXTRA fields rendered on top of the common
// contact block (name/email/phone/contact_preference) and the common consent
// footer. All extra values are posted under `type_data` on the API so the
// backend stays schema-agnostic.
//
// Field types supported: text | number | textarea | select | checkbox | date
// A `required` field with `type: "checkbox"` means it MUST be ticked.

import { Home, Calculator, Search, Key, Building2, FileText, HardHat, Truck, Leaf } from "lucide-react";

export const LEAD_TYPE_LABEL = {
  sale: "Verkoop",
  mortgage: "Hypotheek",
  buying_agent: "Aankoopmakelaar",
  rental: "Verhuur",
  property_management: "Vastgoedbeheer",
  appraisal: "Taxatie",
  inspection: "Bouwkundige keuring",
  moving: "Verhuisservice",
  energy_label: "Energielabel / verduurzaming",
};

export const ALL_LEAD_TYPES = Object.keys(LEAD_TYPE_LABEL);

// ---------- Shared option lists ----------
const TIMELINES = [
  { v: "0-3m", l: "Binnen 3 maanden" },
  { v: "3-6m", l: "Binnen 3-6 maanden" },
  { v: "6-12m", l: "Binnen 6-12 maanden" },
  { v: "12m+", l: "Over een jaar of later" },
  { v: "unknown", l: "Weet ik nog niet" },
];
const PROPERTY_TYPES = ["Appartement", "Tussenwoning", "Hoekwoning", "2-onder-1-kap", "Vrijstaand", "Studio", "Anders"];
const FURNISHING = [
  { v: "unfurnished", l: "Gestoffeerd" },
  { v: "shell", l: "Casco" },
  { v: "upholstered", l: "Gemeubileerd" },
];
const INSPECTION_TIMING = [
  { v: "before_purchase", l: "Voor aankoop" },
  { v: "after_purchase", l: "Na aankoop" },
  { v: "periodic", l: "Periodiek / onderhoud" },
];
const APPRAISAL_PURPOSE = [
  { v: "mortgage", l: "Voor hypotheek" },
  { v: "sale", l: "Voor verkoop" },
  { v: "divorce_estate", l: "Scheiding / nalatenschap" },
  { v: "other", l: "Anders" },
];
const ENERGY_LABELS = ["A++++", "A+++", "A++", "A+", "A", "B", "C", "D", "E", "F", "G", "Weet ik niet"];

// ---------- Form schemas per lead_type ----------
export const LEAD_FORMS = {
  sale: {
    icon: Home,
    cta: "Gratis waardebepaling",
    title: "Woning verkopen? Vraag gratis waardebepaling aan",
    subtitle: "Een lokale makelaar kan je vrijblijvend helpen met de waardebepaling van je woning.",
    modalTitle: "Gratis waardebepaling aanvragen",
    modalSubtitle: "Een lokale makelaar neemt binnen 1-2 werkdagen contact met je op.",
    successTitle: "Bedankt! We nemen snel contact met je op.",
    successBody: "Een lokale makelaar neemt binnen 1-2 werkdagen contact met je op voor een gratis waardebepaling.",
    submitLabel: "Vraag gratis waardebepaling aan",
    address: "full",
    fields: [
      { key: "property_type", label: "Type woning", type: "select", options: PROPERTY_TYPES.map((p) => ({ v: p, l: p })) },
      { key: "estimated_value", label: "Geschatte waarde (€)", type: "number", placeholder: "450000" },
      { key: "selling_timeline", label: "Wanneer wil je verkopen?", type: "select", options: TIMELINES, span: 2 },
      { key: "reason", label: "Reden (optioneel)", type: "text", placeholder: "Verhuizing, upgrading, …", span: 2 },
      { key: "has_broker", label: "Ik heb al een makelaar in de arm genomen", type: "checkbox", span: 2 },
    ],
  },
  mortgage: {
    icon: Calculator,
    cta: "Vraag hypotheekadvies",
    title: "Benieuwd wat je kunt lenen? Gratis hypotheekadvies",
    subtitle: "Een onafhankelijk adviseur rekent voor jou de maximale hypotheek en bijbehorende maandlast uit.",
    modalTitle: "Gratis hypotheekadvies",
    modalSubtitle: "Een adviseur neemt binnen 1-2 werkdagen contact met je op.",
    successTitle: "Bedankt!",
    successBody: "Een hypotheekadviseur neemt binnen 1-2 werkdagen contact met je op.",
    submitLabel: "Vraag hypotheekadvies aan",
    address: "none",
    fields: [
      { key: "income_bracket", label: "Bruto jaarinkomen", type: "select", options: [
        { v: "<40k", l: "Minder dan €40.000" }, { v: "40-60k", l: "€40.000 - €60.000" },
        { v: "60-80k", l: "€60.000 - €80.000" }, { v: "80-120k", l: "€80.000 - €120.000" },
        { v: "120k+", l: "Meer dan €120.000" },
      ]},
      { key: "mortgage_amount", label: "Gewenst hypotheekbedrag (€)", type: "number", placeholder: "350000" },
      { key: "first_time_buyer", label: "Ik ben starter op de woningmarkt", type: "checkbox", span: 2 },
    ],
  },
  buying_agent: {
    icon: Search,
    cta: "Vind een aankoopmakelaar",
    title: "Een aankoopmakelaar aan jouw kant",
    subtitle: "Laat je begeleiden door een lokale aankoopmakelaar. Vooral in deze markt bespaart dat tijd én geld.",
    modalTitle: "Aankoopmakelaar aanvragen",
    modalSubtitle: "Een aankoopmakelaar in jouw regio neemt binnen 1-2 werkdagen contact met je op.",
    successTitle: "Aangekomen!",
    successBody: "Een aankoopmakelaar in jouw regio neemt binnen 1-2 werkdagen contact met je op.",
    submitLabel: "Vraag aankoopmakelaar aan",
    address: "city_only",
    fields: [
      { key: "search_region", label: "Zoekregio / stad", type: "text", placeholder: "Amsterdam, Haarlem", span: 2 },
      { key: "budget_min", label: "Budget min (€)", type: "number", placeholder: "300000" },
      { key: "budget_max", label: "Budget max (€)", type: "number", placeholder: "450000" },
      { key: "property_type", label: "Gewenst woningtype", type: "select", options: PROPERTY_TYPES.map((p) => ({ v: p, l: p })) },
      { key: "num_rooms", label: "Aantal kamers (min)", type: "number", placeholder: "3" },
      { key: "selling_timeline", label: "Wanneer wil je kopen?", type: "select", options: TIMELINES, span: 2 },
      { key: "mortgage_approved", label: "Ik heb al een hypotheekofferte of principe-toezegging", type: "checkbox", span: 2 },
    ],
  },
  rental: {
    icon: Key,
    cta: "Vind een verhuurmakelaar",
    title: "Woning verhuren? Vind een verhuurmakelaar",
    subtitle: "Een verhuurmakelaar vindt snel betrouwbare huurders en regelt alles van screening tot contract.",
    modalTitle: "Verhuurmakelaar aanvragen",
    modalSubtitle: "Een verhuurmakelaar in jouw regio neemt contact met je op.",
    successTitle: "Bedankt!",
    successBody: "Een verhuurmakelaar neemt binnen 1-2 werkdagen contact met je op.",
    submitLabel: "Vraag verhuurmakelaar aan",
    address: "full",
    fields: [
      { key: "property_type", label: "Type woning", type: "select", options: PROPERTY_TYPES.map((p) => ({ v: p, l: p })) },
      { key: "estimated_rent", label: "Gewenste huur (€/mnd)", type: "number", placeholder: "1850" },
      { key: "available_from", label: "Beschikbaar vanaf", type: "date" },
      { key: "furnishing", label: "Afwerking", type: "select", options: FURNISHING },
      { key: "has_broker", label: "Ik heb al een verhuurmakelaar", type: "checkbox", span: 2 },
    ],
  },
  property_management: {
    icon: Building2,
    cta: "Vraag vastgoedbeheer aan",
    title: "Vastgoedbeheer uitbesteden",
    subtitle: "Laat verhuur, huurincasso en onderhoud regelen door een professioneel vastgoedbeheerder.",
    modalTitle: "Vastgoedbeheer aanvragen",
    modalSubtitle: "Een vastgoedbeheerder neemt binnen 1-2 werkdagen contact met je op.",
    successTitle: "Bedankt!",
    successBody: "Een vastgoedbeheerder neemt binnen 1-2 werkdagen contact met je op.",
    submitLabel: "Vraag vastgoedbeheer aan",
    address: "city_only",
    fields: [
      { key: "num_properties", label: "Aantal panden", type: "number", placeholder: "1" },
      { key: "total_rooms", label: "Totaal kamers / units", type: "number", placeholder: "4" },
      { key: "services_needed", label: "Gewenste diensten", type: "textarea", placeholder: "Verhuur, huurincasso, technisch onderhoud, …", span: 2 },
    ],
  },
  appraisal: {
    icon: FileText,
    cta: "Vraag taxatie aan",
    title: "Erkende taxatie van je woning",
    subtitle: "Officiële woningtaxatie door een gecertificeerd taxateur voor hypotheek, verkoop of nalatenschap.",
    modalTitle: "Taxatie aanvragen",
    modalSubtitle: "Een taxateur neemt binnen 1-2 werkdagen contact met je op voor een afspraak.",
    successTitle: "Bedankt!",
    successBody: "Een taxateur neemt binnen 1-2 werkdagen contact met je op.",
    submitLabel: "Vraag taxatie aan",
    address: "full",
    fields: [
      { key: "property_type", label: "Type woning", type: "select", options: PROPERTY_TYPES.map((p) => ({ v: p, l: p })) },
      { key: "appraisal_purpose", label: "Doel van de taxatie", type: "select", options: APPRAISAL_PURPOSE, span: 2 },
      { key: "estimated_value", label: "Indicatieve waarde (€, optioneel)", type: "number", placeholder: "450000", span: 2 },
    ],
  },
  inspection: {
    icon: HardHat,
    cta: "Vraag bouwkundige keuring aan",
    title: "Bouwkundige keuring",
    subtitle: "Weet precies in welke staat de woning is vóór of ná aankoop — door een onafhankelijk bouwkundige.",
    modalTitle: "Bouwkundige keuring aanvragen",
    modalSubtitle: "Een bouwkundige neemt binnen 1-2 werkdagen contact met je op.",
    successTitle: "Bedankt!",
    successBody: "Een bouwkundige neemt binnen 1-2 werkdagen contact met je op.",
    submitLabel: "Vraag keuring aan",
    address: "full",
    fields: [
      { key: "property_type", label: "Type woning", type: "select", options: PROPERTY_TYPES.map((p) => ({ v: p, l: p })) },
      { key: "construction_year", label: "Bouwjaar (indien bekend)", type: "number", placeholder: "1965" },
      { key: "inspection_timing", label: "Wanneer heb je het rapport nodig?", type: "select", options: INSPECTION_TIMING, span: 2 },
    ],
  },
  moving: {
    icon: Truck,
    cta: "Vraag verhuisofferte aan",
    title: "Een stressvrije verhuizing",
    subtitle: "Vraag vrijblijvend offertes op bij lokale verhuisbedrijven — inclusief opslag en demontage indien gewenst.",
    modalTitle: "Verhuisofferte aanvragen",
    modalSubtitle: "Een verhuisbedrijf neemt binnen 1-2 werkdagen contact met je op.",
    successTitle: "Bedankt!",
    successBody: "Een verhuisbedrijf neemt binnen 1-2 werkdagen contact met je op.",
    submitLabel: "Vraag verhuisofferte aan",
    address: "city_only",
    fields: [
      { key: "from_address", label: "Vanaf (plaats/postcode)", type: "text", placeholder: "Amsterdam" },
      { key: "to_address", label: "Naar (plaats/postcode)", type: "text", placeholder: "Haarlem" },
      { key: "volume_m3", label: "Volume (m³, geschat)", type: "number", placeholder: "25" },
      { key: "moving_date", label: "Gewenste verhuisdatum", type: "date" },
      { key: "extras", label: "Extra diensten (opslag, demontage, …)", type: "textarea", span: 2 },
    ],
  },
  energy_label: {
    icon: Leaf,
    cta: "Energielabel / verduurzamen",
    title: "Energielabel of verduurzaming",
    subtitle: "Vraag een nieuw energielabel aan of ontvang vrijblijvend advies over isolatie, zonnepanelen of warmtepomp.",
    modalTitle: "Energielabel of verduurzaming aanvragen",
    modalSubtitle: "Een specialist neemt binnen 1-2 werkdagen contact met je op.",
    successTitle: "Bedankt!",
    successBody: "Een specialist neemt binnen 1-2 werkdagen contact met je op.",
    submitLabel: "Vraag advies aan",
    address: "full",
    fields: [
      { key: "property_type", label: "Type woning", type: "select", options: PROPERTY_TYPES.map((p) => ({ v: p, l: p })) },
      { key: "current_label", label: "Huidig energielabel", type: "select", options: ENERGY_LABELS.map((l) => ({ v: l, l })) },
      { key: "interest_areas", label: "Waar wil je advies over?", type: "textarea", placeholder: "Isolatie, zonnepanelen, warmtepomp, nieuw label aanvragen…", span: 2 },
    ],
  },
};
